import type { CustomerBankconnectSchemas } from "@wayflyer/api-types";
import { FlyError, Link, Text } from "@wayflyer/flyui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { type AuthCallbackPath, useErrorModal } from "shared-ui";
import { handleContactTeam } from "shared-utils";

import { basiqStartAuthFlowUrl } from "../../urls";

import { BasiqClient } from "./basiq-client";

export interface UseBankConnectWithBasiqProps {
  customerSlug: string;
  nextPath: AuthCallbackPath;
  userId?: string | null;
  enabled: boolean;
  onError?: () => void;
}

export function useBankConnectWithBasiq({
  customerSlug,
  nextPath,
  enabled,
  userId,
  onError,
}: UseBankConnectWithBasiqProps) {
  const intl = useIntl();
  const { openErrorModal, closeErrorModal } = useErrorModal();
  const [basiqClientInitialized, setBasiqClientInitialized] = useState(false);
  const [launchingBasiq, setLaunchingBasiq] = useState(false);
  const [reconnectingConnectionId, setReconnectingConnectionId] = useState<string | null>(null);

  const startAuthFlowPath = useMemo(() => basiqStartAuthFlowUrl(customerSlug, nextPath), [customerSlug, nextPath]);
  const basiqClient = useMemo(() => new BasiqClient({ startAuthFlowPath, userId }), [startAuthFlowPath, userId]);

  const ready = basiqClientInitialized && basiqClient.readyForConsentUI;

  const initBasiq = useCallback(() => {
    if (enabled && !basiqClientInitialized) {
      basiqClient.initForConsentUI().then(() => {
        setBasiqClientInitialized(true);
      });
    }
  }, [basiqClient, basiqClientInitialized, enabled]);

  const displayErrorModal = () => {
    openErrorModal(
      new FlyError({
        title: intl.formatMessage({
          id: "bankconnect: available-title",
          defaultMessage: "Sorry, bank connection is not available at the moment.",
        }),
        message: (
          <FormattedMessage
            id="bankconnect: not-available-message"
            defaultMessage="<p>The service is temporarily unavailable. Please try refreshing the page, and if the problem persists <a>contact our support team</a>.</p>"
            values={{
              p: (chunks) => <Text>{chunks}</Text>,
              a: (chunks) => (
                <Link
                  onClick={() => {
                    closeErrorModal();
                    handleContactTeam();
                  }}
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        ),
        error: basiqClient.consentInitError || new Error("Basiq connection not available"),
      })
    );
    onError?.();
  };

  useEffect(() => {
    if (ready && launchingBasiq) {
      const launch = reconnectingConnectionId
        ? basiqClient.launchReconnectionUI(reconnectingConnectionId)
        : basiqClient.launchConsentUI();
      // launch navigates away on success; if the token fetch it depends on fails first, there's
      // no page reload to reset launchingBasiq, so the connect button would stay stuck loading.
      launch.catch(() => {
        setLaunchingBasiq(false);
        displayErrorModal();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- displayErrorModal is a plain function redefined each render, not a dependency to track
  }, [basiqClient, ready, launchingBasiq, reconnectingConnectionId]);

  function startConnect() {
    if (basiqClient.consentInitError || !enabled) {
      displayErrorModal();
    } else {
      // allows us to trigger startConnect even when basiq not ready
      // when basiq becomes ready and shouldLaunchBasiq is true then
      // the flow gets launched
      setReconnectingConnectionId(null);
      initBasiq();
      setLaunchingBasiq(true);
    }
  }

  function startReconnect(bankConnection: CustomerBankconnectSchemas.BankConnectionSchema) {
    if (basiqClient.consentInitError || !enabled) {
      displayErrorModal();
    } else {
      // allows us to trigger startConnect even when basiq not ready
      // when basiq becomes ready and shouldLaunchBasiq is true then
      // the flow gets launched
      setReconnectingConnectionId(bankConnection.id);
      initBasiq();
      setLaunchingBasiq(true);
    }
  }

  return {
    startConnect,
    startReconnect,
    ready,
    error: basiqClient.consentInitError,
    basiqClient: basiqClient,
    launchingBasiq,
  };
}
